/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module AlertModule
 */
/* eslint-disable max-classes-per-file */

import {
    AfterViewInit,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    Inject,
    Input,
    NgModule,
    NO_ERRORS_SCHEMA,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
} from '@angular/core';

import { CommonModule } from '@angular/common';
import { ClrFormLayout } from '@clr/angular';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import './alert-config-modal.component.less';

import {
    AlertCategory,
    AlertSource,
    AviObjectType,
    IAlertRuleEvent,
    IAlertRuleMetric,
} from 'generated-types';

import {
    IAviDataGridConfig,
} from 'ng/modules/data-grid/components/avi-data-grid/avi-data-grid.types';

import {
    AlertConfig,
    AlertMetricThreshold,
    AlertRuleMetric,
} from 'object-types';

import {
    AlertConfigItem,
    AlertRuleOperatorEnum,
    IAlertConfigConfig,
    ValueNone,
} from 'ajs/modules/alert/factories/alert-config/alert-config.item.factory';

import {
    ActionGroupConfigCollection,
    TActionGroupConfigCollection,
} from 'ajs/modules/alert/factories/action-group-config/action-group-config.collection.factory';

import {
    AlertMetricsCollection,
    TAlertMetricsCollection,
} from 'ajs/modules/alert/factories/alert-metrics/alert-metrics.collection.factory';

import {
    PoolCollection,
    TPoolCollection,
} from 'collections/pool.collection.factory';

import {
    TVirtualServiceCollection,
    VirtualServiceCollection,
} from 'collections/virtual-service.collection.factory';

import { L10nService } from '@vmw/ngx-vip';
import { SharedModule } from 'ng/shared/shared.module';
import { AviFormsModule } from 'ng/modules/avi-forms/avi-forms.module';
import { DataGridModule } from 'ng/modules/data-grid/data-grid.module';
import { GslbModule } from 'ng/modules/gslb/gslb.module';

import {
    ServiceEngineCollection,
    TServiceEngineCollection,
} from 'collections/service-engine.collection.factory';

import {
    GSLBServiceCollection,
    TGSLBServiceCollection,
} from 'ajs/modules/gslb/factories/gslb-service/gslb-service.collection.factory';

import { Collection } from 'ajs/modules/data-model/factories/collection.factory';
import * as globalL10n from 'global-l10n';
import { MessageItem } from 'ajs/modules/data-model/factories/message-item.factory';
import { IAviDropdownOption } from 'standalone-components/avi-dropdown/avi-dropdown.types';
import { createDropdownOption } from 'ng/shared/utils/dropdown.utils';
import * as SystemInfoSelectors from 'ng/root-store/system-info/system-info.selectors';
import * as l10n from './alert-config-modal.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * Interface for object type option configuration.
 */
interface IObjectTypeOption {
    value: AviObjectType | typeof ValueNone;
    label: string;
    collection?: Collection;
    disableCreate?: boolean;
    hideForMetricsSource?: boolean;
}

/**
 * @description Modal Component to create & handle Alerts.
 * Alert configs are triggers that determine whether or not an alert should be generated.
 * @author Dhanashree Sathelkar, Shahab Hashmi
 */
@Component({
    selector: 'alert-config-modal',
    templateUrl: './alert-config-modal.component.html',
})
export class AlertConfigModalComponent implements OnInit, OnDestroy, AfterViewInit {
    /**
     * Template ref for input field of the value in the label pair.
     */
    @ViewChild('eventOccursFieldTemplateRef')
    public eventOccursFieldTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Template ref for value input field.
     */
    @ViewChild('valueUnitsFieldTemplateRef')
    public valueUnitsFieldTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Template ref for metric field.
     */
    @ViewChild('metricFieldTemplateRef')
    public metricFieldTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Template ref for comparator field.
     */
    @ViewChild('comparatorFieldTemplateRef')
    public comparatorFieldTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Template ref for duration field.
     */
    @ViewChild('durationFieldTemplateRef')
    public durationFieldTemplateRef: TemplateRef<HTMLElement>;

    @Input()
    public editable: AlertConfigItem;

    public objectType = AlertConfig;

    /**
     * Object type for AlertMetricThreshold.
     */
    public alertMetricThreshold = AlertMetricThreshold;

    /**
     * Object type for AlertRuleMetric.
     */
    public alertRuleMetric = AlertRuleMetric;

    /**
     * Store hidden value of Alert Category Enum.
     */
    public hiddenAlertCategoryValues = [AlertCategory.WATERMARK];

    /**
     * Store hidden value of Alert Source Enum.
     */
    public hiddenAlertSourceValues = [AlertSource.APP_LOGS, AlertSource.CONN_LOGS];

    /**
     * Object type for AviObjectType.
     */
    public objType = ValueNone;

    private readonly vsCollection: VirtualServiceCollection;

    private readonly poolCollection: PoolCollection;

    public readonly alertMetricsCollection: AlertMetricsCollection;

    public readonly actionGroupConfigCollection: ActionGroupConfigCollection;

    private readonly serviceEngineCollection: ServiceEngineCollection;

    private readonly gslbServiceCollection: GSLBServiceCollection;

    private readonly clusterCollection: Collection;

    public metricsGridConfig: IAviDataGridConfig;

    public eventsLogGridConfig: IAviDataGridConfig;

    private commonGridConfig: IAviDataGridConfig;

    /**
     * Layout for AlertConfig modal clrForm.
     */
    public readonly verticalLayout = ClrFormLayout.VERTICAL;

    public readonly l10nKeys = l10nKeys;

    public readonly globalL10nKeys = globalL10nKeys;

    /**
     * Store placeholder message for collection dropdown.
     */
    public objDropdownPlaceholder: string;

    /**
     * Local Hash of Alert Category enum for template usage.
     */
    public readonly alertCategoryHash = {
        [AlertCategory.REALTIME]: AlertCategory.REALTIME,
        [AlertCategory.ROLLINGWINDOW]: AlertCategory.ROLLINGWINDOW,
    };

    /**
     * Configuration array for object type options.
     */
    private objectTypeOptions: IObjectTypeOption[];

    /**
     * Filtered object type options that should be shown.
     */
    private visibleObjectTypeOptions: IObjectTypeOption[];

    /**
     * Currently selected object type option.
     */
    public currentOption?: IObjectTypeOption;

    /**
     * Dropdown options array for object type selection.
     */
    public objectTypeDropdownOptions: IAviDropdownOption[] = [];

    /**
     * Local Hash of AlertRuleOperator enum for template usage.
     */
    public readonly alertRuleOperatorEnumHash = {
        OPERATOR_AND: AlertRuleOperatorEnum.OPERATOR_AND,
        OPERATOR_OR: AlertRuleOperatorEnum.OPERATOR_OR,
        OPERATOR_NOT: AlertRuleOperatorEnum.OPERATOR_NOT,
    };

    constructor(
        private readonly l10nService: L10nService,
        private readonly store: Store,
        @Inject(ActionGroupConfigCollection)
        ActionGroupConfigCollection: TActionGroupConfigCollection,
        @Inject(VirtualServiceCollection)
        VirtualServiceCollection: TVirtualServiceCollection,
        @Inject(PoolCollection)
        PoolCollection: TPoolCollection,
        @Inject(AlertMetricsCollection)
        AlertMetricsCollection: TAlertMetricsCollection,
        @Inject(ServiceEngineCollection)
        ServiceEngineCollection: TServiceEngineCollection,
        @Inject(GSLBServiceCollection)
        GSLBServiceCollection: TGSLBServiceCollection,
        @Inject('Collection')
        CollectionClass: typeof Collection,
    ) {
        this.actionGroupConfigCollection = new ActionGroupConfigCollection();
        this.vsCollection = new VirtualServiceCollection();
        this.alertMetricsCollection = new AlertMetricsCollection();
        this.poolCollection = new PoolCollection({ isStatic: true });
        this.serviceEngineCollection = new ServiceEngineCollection();
        this.gslbServiceCollection = new GSLBServiceCollection();
        this.clusterCollection = new CollectionClass({
            objectName: 'vimgrclusterruntime',
        });
        l10nService.registerSourceBundles(dictionary);
        this.initializeObjectTypeOptions();
    }

    /**
     * Get source from editable.
     */
    public get source(): AlertSource {
        return this.configData.source;
    }

    /**
     * Inform if multiple rules exist.
     */
    public get showMultipleOperators(): boolean {
        if (this.editable.metricRules.count ||
            this.editable.sysEventRules.count) {
            return true;
        }

        return false;
    }

    /**
     * Validate form when no event exists.
     */
    public get isFormInvalid(): boolean {
        return !this.editable.sysEventRules.count && !this.editable.metricRules.count;
    }

    /**
     * Set objRequired boolean when conditions met.
     */
    public get objRequired(): boolean {
        if (this.source === AlertSource.METRICS && !this.configData.autoscale_alert) {
            return true;
        }

        return false;
    }

    /**
     * Check if object creation should be disabled.
     */
    public get isGslbServiceCreateDisabled(): boolean {
        return this.currentOption?.disableCreate ?? false;
    }

    /** @override */
    public ngOnInit(): void {
        this.objDropdownPlaceholder = this.setObjDropdownPlaceholder();
        this.objType = this.editable.config.object_type ||
            (this.source === AlertSource.EVENT_LOGS ? ValueNone : undefined);
        this.updateVisibleObjectTypeOptions();
        this.updateObjectTypeDropdownOptions();
        this.updateCurrentOption();
    }

    /** @override */
    public ngAfterViewInit(): void {
        const { l10nService } = this;

        const {
            metricOccursInputLabel,
            comparatorInputLabel,
            durationLabel,
            valueUnitsLabel,
            eventOccursInputLabel,
        } = l10nKeys;

        const {
            noItemsFoundLabel,
            deleteLabel,
        } = globalL10nKeys;

        this.commonGridConfig = {
            fields: [],
            layout: {
                placeholderMessage: l10nService.getMessage(noItemsFoundLabel),
            },
        };

        this.metricsGridConfig = {
            ...this.commonGridConfig,
            fields: [
                {
                    label: l10nService.getMessage(metricOccursInputLabel),
                    id: 'enabled',
                    templateRef: this.metricFieldTemplateRef,
                },
                {
                    label: l10nService.getMessage(comparatorInputLabel),
                    id: 'comparator',
                    templateRef: this.comparatorFieldTemplateRef,
                },
                {
                    label: l10nService.getMessage(durationLabel),
                    id: 'duration',
                    templateRef: this.durationFieldTemplateRef,
                },
                {
                    label: l10nService.getMessage(valueUnitsLabel),
                    id: 'value',
                    templateRef: this.valueUnitsFieldTemplateRef,
                },
            ],
            singleactions: [
                {
                    label: l10nService.getMessage(deleteLabel),
                    shape: 'trash',
                    onClick: (metricRow: MessageItem<IAlertRuleMetric>) => {
                        return this.editable.deleteMetricRow(metricRow);
                    },
                },
            ],
            multipleactions: [
                {
                    label: l10nService.getMessage(globalL10nKeys.deleteLabel),
                    onClick: (metricRows: Array<MessageItem<IAlertRuleMetric>>) => {
                        metricRows.forEach((metricRow: MessageItem<IAlertRuleMetric>) => {
                            this.editable.deleteMetricRow(metricRow);
                        });
                    },
                },
            ],
        };

        this.eventsLogGridConfig = {
            ...this.commonGridConfig,
            fields: [
                {
                    label: l10nService.getMessage(eventOccursInputLabel),
                    id: 'event-occurs',
                    templateRef: this.eventOccursFieldTemplateRef,
                },
            ],
            singleactions: [
                {
                    label: l10nService.getMessage(deleteLabel),
                    shape: 'trash',
                    onClick: (sysRuleEvent: MessageItem<IAlertRuleEvent>) => {
                        return this.editable.deleteEventRow(sysRuleEvent);
                    },
                },
            ],
            multipleactions: [
                {
                    label: l10nService.getMessage(globalL10nKeys.deleteLabel),
                    onClick: (sysRuleEvents: Array<MessageItem<IAlertRuleEvent>>) => {
                        sysRuleEvents.forEach((sysRuleEvent: MessageItem<IAlertRuleEvent>) => {
                            this.editable.deleteEventRow(sysRuleEvent);
                        });
                    },
                },
            ],
        };
    }

    /** @override */
    public ngOnDestroy(): void {
        this.actionGroupConfigCollection.destroy();
        this.alertMetricsCollection.destroy();
        this.vsCollection.destroy();
        this.poolCollection.destroy();
        this.serviceEngineCollection.destroy();
        this.gslbServiceCollection.destroy();
        this.clusterCollection.destroy();
    }

    /**
     * Handle source change and update visible options.
     */
    public handleSourceChange(): void {
        this.editable.handleSourceChange();
        this.objType = this.source === AlertSource.EVENT_LOGS ? ValueNone : undefined;
        this.updateVisibleObjectTypeOptions();
        this.updateObjectTypeDropdownOptions();
        this.updateCurrentOption();
        this.objDropdownPlaceholder = this.setObjDropdownPlaceholder();
    }

    public handleObjectTypeChange(): void {
        const objectType = this.objType === ValueNone ? undefined : this.objType;

        this.editable.setObjectType(objectType);
        this.updateCurrentOption();
    }

    /**
     * Return config data.
     */
    private get configData(): IAlertConfigConfig {
        return this.editable.getConfig();
    }

    /**
     * Initialize object type options array.
     */
    private initializeObjectTypeOptions(): void {
        this.objectTypeOptions = [
            {
                value: ValueNone,
                label: this.l10nService.getMessage(globalL10nKeys.emptyLabel),
                hideForMetricsSource: true,
                disableCreate: true,
            },
            {
                value: AviObjectType.VIRTUALSERVICE,
                label: this.l10nService.getMessage(l10nKeys.virtualServiceRadioOption),
                collection: this.vsCollection,
            },
            {
                value: AviObjectType.SERVICEENGINE,
                label: this.l10nService.getMessage(globalL10nKeys.serviceEngineLabel),
                collection: this.serviceEngineCollection,
                disableCreate: true,
            },
            {
                value: AviObjectType.POOL,
                label: this.l10nService.getMessage(globalL10nKeys.poolLabel),
                collection: this.poolCollection,
            },
            {
                value: AviObjectType.GSLBSERVICE,
                label: this.l10nService.getMessage(globalL10nKeys.gslbServicesLabel),
                collection: this.gslbServiceCollection,
            },
            {
                value: AviObjectType.CLUSTER,
                label: this.l10nService.getMessage(globalL10nKeys.clusterLabel),
                collection: this.clusterCollection,
                disableCreate: true,
            },
        ];
    }

    /**
     * Update visible object type options based on current source.
     */
    private updateVisibleObjectTypeOptions(): void {
        let localSiteIsGslbLeader = false;

        this.store.select(SystemInfoSelectors.selectLocalSiteIsGslbLeader)
            .pipe(take(1))
            .subscribe(isLeader => localSiteIsGslbLeader = isLeader);

        this.visibleObjectTypeOptions = this.objectTypeOptions.filter(option => {
            if (option.hideForMetricsSource) {
                return this.source !== AlertSource.METRICS;
            }

            // Filter out GSLB option if local site is not GSLB leader
            if (option.value === AviObjectType.GSLBSERVICE) {
                return localSiteIsGslbLeader;
            }

            return true;
        });
    }

    /**
     * Update dropdown options array from visible object type options.
     */
    private updateObjectTypeDropdownOptions(): void {
        this.objectTypeDropdownOptions = this.visibleObjectTypeOptions.map(option => {
            return createDropdownOption(option.value, option.label);
        });
    }

    /**
     * Update current option based on objType.
     */
    private updateCurrentOption(): void {
        if (this.objType === ValueNone || !this.objType) {
            this.currentOption = undefined;

            return;
        }

        this.currentOption = this.objectTypeOptions.find(opt => opt.value === this.objType);
    }

    /**
     * Return placeholder message when source is Metrics.
     */
    private setObjDropdownPlaceholder(): string {
        if (this.source === AlertSource.METRICS) {
            return this.l10nService.getMessage(globalL10nKeys.emptyLabel);
        }

        return this.l10nService.getMessage(l10nKeys.dropdownAllInstances);
    }
}

@NgModule({
    declarations: [AlertConfigModalComponent],
    imports: [
        AviFormsModule,
        SharedModule,
        CommonModule,
        DataGridModule,
        GslbModule,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
})
// eslint-disable-next-line no-unused-vars
class AlertConfigModule {}
